import { useEffect, useMemo, useState } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';

const normalizeValue = (value) => (value || '').toString().trim().toLowerCase();
const ADMISSION_FEE_ALWAYS_EXEMPT_STATUSES = new Set([
  'completed',
  'deferred',
  'expelled',
  'dropped',
  'paused',
]);

export const hasAdmissionFeeExemptStudentStatus = (studentRecord = {}) => {
  const userStatus = normalizeValue(studentRecord.userStatus);
  const lifecycleStatus = normalizeValue(studentRecord.Status);
  const prospectAdmissionNumber = (studentRecord.prospectAdmissionNumber || '').toString().trim();

  if (
    ADMISSION_FEE_ALWAYS_EXEMPT_STATUSES.has(userStatus)
    || ADMISSION_FEE_ALWAYS_EXEMPT_STATUSES.has(lifecycleStatus)
  ) {
    return true;
  }

  return (
    (userStatus === 'did not join' || lifecycleStatus === 'did not join')
    && !prospectAdmissionNumber
  );
};

export const canContinueTuitionWithoutAdmissionFee = (studentRecord = {}) => {
  const userStatus = normalizeValue(studentRecord.userStatus);
  const lifecycleStatus = normalizeValue(studentRecord.Status);

  return (
    (userStatus === 'student' && lifecycleStatus === 'active')
    || hasAdmissionFeeExemptStudentStatus(studentRecord)
  );
};

const isStudentAdmitted = (studentRecord = {}) => {
  const userStatus = normalizeValue(studentRecord.userStatus);
  const lifecycleStatus = normalizeValue(studentRecord.Status);

  return userStatus === 'student' || lifecycleStatus === 'active' || lifecycleStatus === 'completed';
};

function useAdmissionJourney() {
  const [isLoading, setIsLoading] = useState(true);
  const [studentRecord, setStudentRecord] = useState({});
  const auth = getAuth();

  useEffect(() => {
    let isMounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        if (isMounted) {
          setStudentRecord({});
          setIsLoading(false);
        }
        return;
      }

      setIsLoading(true);

      try {
        const snapshot = await getDocs(query(collection(db, 'users'), where('userId', '==', user.uid)));
        const nextStudentRecord = snapshot.empty
          ? {}
          : {
            id: snapshot.docs[0].id,
            ...snapshot.docs[0].data(),
          };

        if (isMounted) {
          setStudentRecord(nextStudentRecord);
        }
      } catch (error) {
        console.error('Failed to load admission journey state:', error);
        if (isMounted) {
          setStudentRecord({});
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, [auth]);

  return useMemo(() => {
    const prospectAdmissionNumber = (studentRecord.prospectAdmissionNumber || '').toString().trim();
    const isAdmissionFeePaid = normalizeValue(studentRecord.admissionFeeStatus) === 'paid';
    const admitted = isStudentAdmitted(studentRecord);
    const isAdmissionFeeExempt = hasAdmissionFeeExemptStudentStatus(studentRecord);

    return {
      isLoading,
      studentRecord,
      isAdmitted: admitted,
      isAdmissionFeePaid,
      isAdmissionFeeExempt,
      canContinueTuitionWithoutAdmissionFee: canContinueTuitionWithoutAdmissionFee(studentRecord),
      hasProspectAdmissionNumber: Boolean(prospectAdmissionNumber),
      prospectAdmissionNumber,
      shouldShowIncompleteAdmissionBanner: !isLoading && !admitted && !isAdmissionFeeExempt,
    };
  }, [isLoading, studentRecord]);
}

export default useAdmissionJourney;
